// =======================================================
// PulseGrid — beat-synced grid with radial waves and flashing cells
// =======================================================

import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform float uLoopPhase;
  uniform float uBeats;
  uniform vec2  uResolution;
  uniform vec3  uColor;
  uniform vec3  uBg;
  uniform float uCell;     // cell size in px
  uniform float uWave;     // radial wave strength
  uniform float uRandom;   // random hot-cell amount
  uniform float uLineTh;   // line thickness in px
  uniform float uFill;     // base cell fill
  uniform float uBar;      // bottom beat bar

  ${GLSL_COMMON}

  void main() {
    vec2 frag = vUv * uResolution;
    vec2 center = uResolution * 0.5;
    float phase = uLoopPhase;

    // grid lines (sharp in screen-space)
    vec2 cellF = frag / uCell;
    vec2 g = fract(cellF) * uCell;
    float lineD = min(min(g.x, uCell - g.x), min(g.y, uCell - g.y));
    float line = smoothstep(uLineTh, 0.0, lineD) * 0.08;

    // cell centroid, in cell-index space
    vec2 cellIdx = floor(cellF);
    vec2 cellCenter = (cellIdx + 0.5) * uCell;

    // radial wave: expanding ring from screen center, pulsing with beat
    float dCenter = length(cellCenter - center);
    float waveCycles = max(1.0, floor(uWave * 4.0 + 0.5));
    float waveT   = (uLoopPhase * waveCycles * 12.0 - dCenter / max(1.0, uCell)) * 0.25;
    float waveA   = max(0.0, sin(waveT * 6.2831)) * exp(-abs(waveT) * 0.4) * uWave;

    // flowing random cells: each cell gets an independent cycle, so new
    // cells fade in while older cells fade out instead of all resetting.
    float h = hash21(cellIdx);
    float h2 = hash21(cellIdx + 17.31);
    float h3 = hash21(cellIdx + 41.73);
    float cellClock = 0.5 + 0.5 * sin((uLoopPhase * mix(0.15, 0.7, h2) + h) * 6.28318530718);
    float cellGate = smoothstep(1.0 - uRandom, 1.0 - uRandom + 0.18, cellClock);
    float cellLife = smoothstep(0.02, 0.42, cellClock) * smoothstep(1.0, 0.58, cellClock);
    float shimmer = 0.72 + 0.28 * sin((uLoopPhase * mix(0.08, 0.35, h3) + h2) * 6.28318530718);
    float flicker = cellGate * cellLife * shimmer * (0.45 + 0.55 * h2);

    // inside-cell shape for fill
    float inCell = step(uLineTh, min(g.x, g.y)) * step(uLineTh, min(uCell - g.x, uCell - g.y));
    float cellFill = (waveA + flicker + uFill * 0.1) * inCell;

    // beat bar along bottom
    float barY = step(uResolution.y - max(2.0, uResolution.y * 0.015), frag.y);
    float bar  = barY * step(frag.x, uResolution.x * phase) * uBar;

    float b = line + cellFill + bar;
    vec3 col = mix(uBg, uColor, clamp(b * 1.25, 0.0, 1.5));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class PulseGrid extends Generator {
  constructor() {
    super({
      name: 'Pulse Grid',
      category: 'intense',
      schema: {
        cell:     { default: 40,  min: 4,    max: 240, step: 1,    label: 'Cell size (px)' },
        wave:     { default: 0.6, min: 0,    max: 2,   step: 0.01, label: 'Radial wave' },
        random:   { default: 0.28, min: 0,   max: 1,   step: 0.01, label: 'Random cells' },
        lineTh:   { default: 1.2, min: 0.3,  max: 6,   step: 0.1,  label: 'Line thickness' },
        fill:     { default: 0.04, min: 0,   max: 1,   step: 0.01, label: 'Base fill' },
        bar:      { default: 0.08, min: 0,   max: 1,   step: 0.01, label: 'Beat bar' },
        accent:   { default: '#62ffd8', type: 'color', label: 'Color' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;
    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uLoopPhase:  { value: 0 },
        uBeats:      { value: engine.timeline.beats },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uColor:      { value: new THREE.Color(this.v('accent')) },
        uBg:         { value: new THREE.Color('#000000') },
        uCell:       { value: this.v('cell') },
        uWave:       { value: this.v('wave') },
        uRandom:     { value: this.v('random') },
        uLineTh:     { value: this.v('lineTh') },
        uFill:       { value: this.v('fill') },
        uBar:        { value: this.v('bar') },
      },
      transparent: false, depthWrite: false, depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
  }

  onResize(w, h) {
    if (this.mesh) this.mesh.material.uniforms.uResolution.value.set(w, h);
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'accent') u.uColor.value.set(val);
    else if (`u${key[0].toUpperCase()}${key.slice(1)}` in u) {
      u[`u${key[0].toUpperCase()}${key.slice(1)}`].value = val;
    }
  }

  update(dt, time, engine) {
    if (!this.mesh) return;
    const loop = engine.getLoopInfo(time);
    const u = this.mesh.material.uniforms;
    u.uLoopPhase.value = loop.phase;
    u.uBeats.value = loop.beats;
  }
}
